import React, { useEffect, useState } from "react";
import { perfDiagnostic, prewarmStats, fallbackHistogram } from "@/lib/perfLog";
import api from "@/lib/api";

/**
 * ViewerDebugPanel — admin/preview-only HUD that surfaces the active scene
 * + video element state + transition FSM so playback issues can be
 * diagnosed without cracking open the browser console.
 *
 * Visibility:
 *   - shown if URL contains ?debug=1
 *   - shown if `localStorage.token` looks like a logged-in admin session
 *   - otherwise NEVER rendered (public users see nothing)
 */
export function shouldShowDebugPanel() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("debug") === "1") return true;
    if (params.get("diag") === "1") return true;
    return !!localStorage.getItem("token");
  } catch (_) { return false; }
}

const READY_STATE_LABEL = ["HAVE_NOTHING", "HAVE_METADATA", "HAVE_CURRENT_DATA", "HAVE_FUTURE_DATA", "HAVE_ENOUGH_DATA"];
const NETWORK_STATE_LABEL = ["EMPTY", "IDLE", "LOADING", "NO_SOURCE"];

export default function ViewerDebugPanel({ scene, status, transitionMode, pendingTransitionId }) {
  // Subscribe to window.__viewerPerf so we always show the
  // most-recent fallback reason. Poll at 500ms — debug HUD only.
  const [lastFallback, setLastFallback] = useState(null);
  const [lastPlay, setLastPlay] = useState(null);
  const [fsmState, setFsmState] = useState(null);
  const [pwStats, setPwStats] = useState({ total: 0, hits: 0, misses: 0, hitRate: null });
  const [fbHist, setFbHist] = useState({});
  const [copyState, setCopyState] = useState("idle"); // idle | ok | fail
  const [sendState, setSendState] = useState("idle"); // idle | sending | code | fail
  const [sentCode, setSentCode] = useState(null);
  useEffect(() => {
    const tick = () => {
      const store = window.__viewerPerf || {};
      const fbs = store.fallbacks || [];
      setLastFallback(fbs.length ? fbs[fbs.length - 1] : null);
      const plays = store.playResults || [];
      setLastPlay(plays.length ? plays[plays.length - 1] : null);
      const el = document.querySelector('[data-testid="viewer-scrub-overlay"]');
      setFsmState(el?.getAttribute("data-fsm-state") || null);
      setPwStats(prewarmStats());
      setFbHist(fallbackHistogram());
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, []);

  const copyDiagnostic = async () => {
    try {
      const text = JSON.stringify(perfDiagnostic(), null, 2);
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-clipboard browsers (older iOS Safari): drop in a
        // textarea, select, execCommand('copy'). Best-effort.
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); ta.remove();
      }
      setCopyState("ok");
      setTimeout(() => setCopyState("idle"), 1500);
    } catch (_) {
      setCopyState("fail");
      setTimeout(() => setCopyState("idle"), 2000);
    }
  };

  const sendDiagnostic = async () => {
    setSendState("sending");
    try {
      const r = await api.post("/public/diagnostics", { payload: perfDiagnostic() });
      setSentCode(r?.data?.code || null);
      setSendState("code");
    } catch (_) {
      setSendState("fail");
      setTimeout(() => setSendState("idle"), 2500);
    }
  };

  if (!scene) return null;
  const err = status?.error;
  const row = (k, v, cls = "") => (
    <div className="flex gap-3" key={k}>
      <span className="text-zinc-500 w-28 shrink-0">{k}</span>
      <span className={`text-white/95 break-all ${cls}`}>{v ?? "—"}</span>
    </div>
  );
  return (
    <div
      data-testid="viewer-debug-panel"
      className="absolute top-20 right-4 z-50 bg-black/85 text-white text-[11px] leading-relaxed rounded-lg px-3 py-2.5 max-w-xs font-mono shadow-lg border border-white/10"
      style={{ pointerEvents: "auto" }}
    >
      <div className="flex items-center justify-between mb-1.5 pb-1.5 border-b border-white/15">
        <span className="uppercase tracking-[0.2em] text-amber-400/90">debug</span>
        <span className="text-zinc-400">{transitionMode ? `transition: ${transitionMode}` : "scene"}</span>
      </div>
      {row("scene", scene.id)}
      {row("name", scene.name)}
      {row("type", scene.type)}
      {row("media", short(scene.media_url))}
      {row("poster", short(scene.poster_url))}
      {row("display", scene.display_mode || "contain")}
      <div className="h-1.5" />
      {row("state", status ? (status.paused ? (status.everPlayed ? "paused" : "never_played") : "playing") : "—",
        status && status.paused && !status.everPlayed ? "text-rose-300" : ""
      )}
      {row("readyState", status?.readyState != null ? `${status.readyState} ${READY_STATE_LABEL[status.readyState] || ""}` : "—")}
      {row("networkState", status?.networkState != null ? `${status.networkState} ${NETWORK_STATE_LABEL[status.networkState] || ""}` : "—")}
      {row("loadedmeta", status?.loadedMetadata ? "yes" : "no")}
      {row("canplay", status?.canPlay ? "yes" : "no")}
      {row("currentTime", status ? status.currentTime?.toFixed(2) : "—")}
      {row("duration", status ? (status.duration && isFinite(status.duration) ? status.duration.toFixed(2) : "—") : "—")}
      {row("size", status && status.videoWidth ? `${status.videoWidth}×${status.videoHeight}` : "—")}
      {err && (
        <div className="mt-1.5 pt-1.5 border-t border-rose-500/40 text-rose-300">
          {row("error code", err.code)}
          {row("error msg", err.message || "(none)")}
        </div>
      )}
      {pendingTransitionId && (
        <div className="mt-1.5 pt-1.5 border-t border-white/15 text-amber-300">
          {row("transition", pendingTransitionId)}
          {row("fsm", fsmState || "—")}
        </div>
      )}
      {lastPlay && (
        <div className="mt-1.5 pt-1.5 border-t border-white/15" data-testid="debug-last-play">
          {row("play",
            `${lastPlay.label} → ${lastPlay.ok ? "ok" : "denied"}`,
            lastPlay.ok ? "text-emerald-300" : "text-rose-300"
          )}
          {!lastPlay.ok && row("play err", `${lastPlay.name || ""}: ${lastPlay.message || ""}`, "text-rose-300/85")}
        </div>
      )}
      {lastFallback && (
        <div className="mt-1.5 pt-1.5 border-t border-amber-500/40 text-amber-200" data-testid="debug-last-fallback">
          {row("fallback", lastFallback.reason)}
          {row("fb details", JSON.stringify(lastFallback.details || {}))}
        </div>
      )}
      <div className="mt-1.5 pt-1.5 border-t border-white/15" data-testid="debug-prewarm-stats">
        {row(
          "prewarm",
          pwStats.total
            ? `${pwStats.hits}/${pwStats.total} hit${pwStats.hitRate != null ? ` (${(pwStats.hitRate * 100).toFixed(0)}%)` : ""}`
            : "—",
          pwStats.misses ? "text-amber-300" : ""
        )}
      </div>
      {Object.keys(fbHist).length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-white/15" data-testid="debug-fallback-hist">
          {Object.entries(fbHist).map(([reason, count]) => row(reason, count, "text-amber-200"))}
        </div>
      )}
      <div className="mt-2 pt-1.5 border-t border-white/15 space-y-1.5">
        <button
          onClick={copyDiagnostic}
          data-testid="debug-copy-diagnostic"
          className="w-full text-[10px] uppercase tracking-[0.2em] px-2 py-1.5 rounded bg-white/10 hover:bg-white/15 text-white/90 transition"
        >
          {copyState === "ok" ? "Copied ✓" : copyState === "fail" ? "Copy failed" : "Copy diagnostic"}
        </button>
        <button
          onClick={sendDiagnostic}
          disabled={sendState === "sending"}
          data-testid="debug-send-diagnostic"
          className="w-full text-[10px] uppercase tracking-[0.2em] px-2 py-1.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 transition disabled:opacity-60"
        >
          {sendState === "sending" ? "Sending…" : sendState === "fail" ? "Send failed — retry" : "Send to support"}
        </button>
        {sendState === "code" && sentCode && (
          <div
            data-testid="debug-diagnostic-code"
            className="mt-1 text-center rounded bg-emerald-500/15 border border-emerald-400/30 px-2 py-1.5"
          >
            <div className="text-[9px] uppercase tracking-[0.2em] text-emerald-300/80">Share this code with support</div>
            <div className="text-base font-bold tracking-[0.3em] text-emerald-200 select-all">{sentCode}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function short(s) {
  if (!s) return null;
  if (s.length <= 56) return s;
  return s.slice(0, 26) + "…" + s.slice(-26);
}
